import { useState, type RefObject } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useWorkspaces } from "@/features/workspaces/useWorkspaces";
import type { LocalWorkflow } from "@/features/workspaces/localWorkflows";
import { useAuth } from "@/features/auth/AuthContext";
import { useNavigation } from "@/features/navigation/useNavigation";
import { useNavigationPreferences } from "@/features/navigation/useNavigationPreferences";
import { NavigationIcon } from "@/features/navigation/NavigationIcon";
import { IconChevronRight, IconCpu, IconFactory, IconGrid, IconSearch, IconSettings, IconStar, IconX } from "@/lib/icons";
import "./Sidebar.css";

interface SidebarProps {
  activeWorkflowId: string | null;
  mobileOpen?: boolean;
  mobileMode?: boolean;
  containerRef?: RefObject<HTMLElement>;
  onMobileClose?: () => void;
}

/** Navegação da organização; somente IDs pessoais ficam nos favoritos. */
export function Sidebar(props: SidebarProps) {
  const { usuario, aberto, permissoes } = useAuth();
  // Remontar na troca da identidade elimina busca/expansões transitórias.
  return <SidebarContent key={`${usuario?.id ?? (aberto ? "bancada" : "anonimo")}:${usuario?.organizacao?.organizationId ?? ""}`}
    {...props} canManage={!aberto && (permissoes.gerenciarWorkspaces || permissoes.gerenciarPessoas)} />;
}

function SidebarContent({ activeWorkflowId, mobileOpen = false, mobileMode = false, containerRef, onMobileClose, canManage }: SidebarProps & { canManage: boolean }) {
  const { workspaces, carregando, erro, recarregar } = useWorkspaces();
  const { usuario, aberto } = useAuth();
  const navigation = useNavigation();
  const preferences = useNavigationPreferences();
  const { pathname } = useLocation();
  const [search, setSearch] = useState("");
  const [temporaryGroups, setTemporaryGroups] = useState<Record<string, boolean>>({});
  const query = normalizeSearch(search);
  const workflowMap = new Map(workspaces.map(workflow => [workflow.id, workflow]));
  const assigned = new Set<string>();
  const groups = (navigation.config?.groups ?? []).map(group => ({
    ...group,
    workflows: group.workflowIds.flatMap(id => {
      const workflow = workflowMap.get(id);
      if (!workflow || assigned.has(id)) return [];
      assigned.add(id);
      return [workflow];
    }),
  })).filter(group => group.workflows.length > 0);
  const matches = (workflow: LocalWorkflow, context = "") =>
    normalizeSearch(`${workflow.name} ${workflow.description ?? ""} ${context}`).includes(query);
  const visibleGroups = groups.map(group => ({ ...group,
    workflows: group.workflows.filter(workflow => matches(workflow, `${group.name} ${group.section ?? ""}`)),
  })).filter(group => group.workflows.length > 0);
  const ungrouped = workspaces.filter(workflow => !assigned.has(workflow.id) && matches(workflow));
  const favorites = preferences.favorites.flatMap(id => {
    const workflow = workflowMap.get(id);
    const group = groups.find(group => group.workflowIds.includes(id));
    return workflow && matches(workflow, `${group?.name ?? ""} ${group?.section ?? ""}`) ? [{ workflow, group }] : [];
  });
  const canSavePreferences = preferences.status === "ready" || preferences.status === "saving";
  const resultCount = visibleGroups.reduce((total, group) => total + group.workflows.length, 0) + ungrouped.length;

  function equipment(workflow: LocalWorkflow, favorite = false, context?: string) {
    const starred = preferences.favorites.includes(workflow.id);
    const active = activeWorkflowId === workflow.id;
    return <div className={`sidebar-equipment${active ? " is-active" : ""}`} key={workflow.id}>
      <NavLink to={`/workflows/${encodeURIComponent(workflow.id)}`} end
        aria-label={`${favorite ? "Favorito" : "Equipamento"} ${workflow.name}`} title={workflow.name}
        className="sidebar-equipment-link" aria-current={active ? "page" : undefined}>
        {favorite ? <IconStar size={14} /> : <span className="sidebar-equipment-dot" aria-hidden />}
        <span><b>{workflow.name}</b>{context ? <small>{context}</small> : null}</span>
      </NavLink>
      <button type="button" className={`sidebar-star${starred ? " is-favorite" : ""}`}
        aria-label={`${starred ? "Remover" : "Adicionar"} ${workflow.name} ${starred ? "dos" : "aos"} favoritos`}
        aria-pressed={starred} title={starred ? "Remover dos meus favoritos" : "Adicionar aos meus favoritos"}
        disabled={!canSavePreferences} onClick={() => void preferences.toggleFavorite(workflow.id)}>
        <IconStar size={14} fill={starred ? "currentColor" : "none"} />
      </button>
    </div>;
  }

  return <aside id="ghm-primary-nav" ref={containerRef}
    className={`sidebar sidebar-operational${mobileOpen ? " is-open" : ""}`}
    aria-label="Navegação principal" role={mobileMode && mobileOpen ? "dialog" : undefined}
    aria-modal={mobileMode && mobileOpen ? true : undefined} aria-hidden={mobileMode && !mobileOpen ? true : undefined}
    {...{ inert: mobileMode && !mobileOpen ? "" : undefined }}
    onClick={event => {
      if (mobileMode && mobileOpen && !event.ctrlKey && !event.metaKey && !event.shiftKey &&
        event.target instanceof Element && event.target.closest("a[href]")) onMobileClose?.();
    }}>
    <div className="sidebar-top">
      <div className="brand-row">
        <Link to="/workflows" className="brand" aria-label="GHM · Visão geral">
          <div className="brand-mark" aria-hidden>GHM</div>
          <div className="brand-text"><b>GHM</b><small>Industrial Intel</small></div>
        </Link>
        {mobileMode && onMobileClose ? <button type="button" className="m-drawer-close" aria-label="Fechar menu" onClick={onMobileClose}><IconX size={18} /></button> : null}
      </div>
      <div className="sidebar-organization"><IconFactory size={18} /><div>
        <b>{usuario?.organizacao?.nome ?? (aberto ? "Bancada local" : "Minha organização")}</b>
        <small>{aberto ? "Ambiente sem login" : "Gestão da operação"}</small>
      </div></div>
      <label className="sidebar-search-field"><IconSearch size={16} />
        <input type="search" aria-label="Buscar equipamentos e áreas" placeholder="Buscar equipamento ou área"
          value={search} onChange={event => setSearch(event.target.value)} />
      </label>
    </div>

    <nav className="sidebar-navigation" aria-label="Operação">
      <NavLink to="/workflows" end aria-label="Visão geral" className={({ isActive }) => `sidebar-main-link${isActive ? " active" : ""}`}>
        <IconGrid size={17} /><span>Visão geral</span>
      </NavLink>
      {preferences.error ? <div className="sidebar-notice" role="status">Não foi possível salvar ou carregar suas preferências.
        <button type="button" onClick={preferences.reload}>Tentar novamente</button></div> : null}
      {!query || favorites.length > 0 ? <section className="sidebar-favorites" aria-label="Meus favoritos">
        <h2 className="sidebar-section-heading">Meus favoritos</h2>
        {favorites.length ? favorites.map(({ workflow, group }) => equipment(workflow, true, group?.name ?? workflow.description))
          : <p className="sidebar-hint">{preferences.status === "loading" ? "Carregando favoritos…" : "Use a estrela para fixar seus equipamentos."}</p>}
      </section> : null}
      <section className="sidebar-areas" aria-label="Áreas e equipamentos">
        <h2 className="sidebar-section-heading">{query ? "Resultados da busca" : "Explorar áreas"}</h2>
        {navigation.error ? <div className="sidebar-notice" role="status">Não foi possível atualizar as áreas.
          <button type="button" onClick={navigation.reload}>Tentar novamente</button></div> : null}
        {erro ? <div className="sidebar-notice" role="status">Não foi possível atualizar os equipamentos.
          <button type="button" onClick={recarregar}>Tentar novamente</button></div> : null}
        {visibleGroups.map((group, index) => {
          const hasActive = group.workflowIds.includes(activeWorkflowId ?? "");
          const savedOpen = preferences.expandedGroups.includes(group.id) ||
            (hasActive && !preferences.collapsedGroups.includes(group.id));
          const open = !!query || (canSavePreferences ? savedOpen : (temporaryGroups[group.id] ?? savedOpen));
          const showSection = group.section && (index === 0 || visibleGroups[index - 1].section !== group.section);
          return <div className="sidebar-area" key={group.id}>
            {showSection ? <h3 className="sidebar-area-section">{group.section}</h3> : null}
            <button type="button" className={`sidebar-area-toggle${hasActive ? " contains-active" : ""}`}
              aria-expanded={open} aria-controls={`sidebar-group-${group.id}`} disabled={!!query}
              onClick={() => {
                if (canSavePreferences) void preferences.setGroupExpanded(group.id, !open);
                else setTemporaryGroups(previous => ({ ...previous, [group.id]: !open }));
              }}>
              <NavigationIcon name={group.icon} /><span>{group.name}</span>
              <small aria-label={`${group.workflows.length} ${group.workflows.length === 1 ? "equipamento" : "equipamentos"}`}>{group.workflows.length}</small>
              <IconChevronRight size={14} />
            </button>
            {open ? <div className="sidebar-area-items" id={`sidebar-group-${group.id}`}>
              {group.workflows.map(workflow => equipment(workflow))}
            </div> : null}
          </div>;
        })}
        {ungrouped.length ? <div className="sidebar-ungrouped">
          <h3 className="sidebar-area-section">{groups.length ? "Sem área" : "Equipamentos"}</h3>
          {ungrouped.map(workflow => equipment(workflow, false, workflow.description))}
        </div> : null}
        {!resultCount ? <p className="sidebar-hint" role="status">{carregando ? "Carregando equipamentos…" : query ? "Nenhum equipamento encontrado." : "Nenhum equipamento cadastrado."}</p> : null}
        {query ? <p className="sidebar-hint" aria-live="polite">{resultCount} equipamento(s) encontrado(s)</p> : null}
      </section>
    </nav>
    <div className="sidebar-bottom">
      {canManage ? <NavLink to="/configuracoes#navegacao" aria-label="Administração" className={({ isActive }) => `sidebar-main-link${isActive ? " active" : ""}`}>
        <IconSettings size={17} /><span>Administração</span>
      </NavLink> : null}
      <Link to={activeWorkflowId ? `/workspaces/${encodeURIComponent(activeWorkflowId)}/devices` : "/devices"}
        aria-label="Diagnóstico técnico" className={`sidebar-main-link sidebar-technical${pathname.startsWith("/devices") || pathname.endsWith("/devices") ? " active" : ""}`}>
        <IconCpu size={16} /><span>Diagnóstico técnico</span>
      </Link>
      <SidebarIdentidade />
    </div>
  </aside>;
}

function SidebarIdentidade() {
  const { usuario, aberto, sair } = useAuth();
  const [saindo, setSaindo] = useState(false);
  return <div className="sb-foot">
    <div className="avatar" aria-hidden>{usuario ? iniciais(usuario.nome) : "OP"}</div>
    <div className="sidebar-account-name"><b>{usuario?.nome ?? "Operador"}</b>
      <small>{usuario ? (usuario.papel === "admin" ? "Administrador" : "Operador") : aberto ? "Bancada local" : "Sessão"}</small>
    </div>
    {usuario ? <button type="button" className="btn ghost sm sb-foot-sair" disabled={saindo} onClick={() => {
      setSaindo(true); void sair().finally(() => setSaindo(false));
    }}>Sair</button> : null}
  </div>;
}

function iniciais(nome: string) {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  return partes.length ? (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase() : "?";
}

function normalizeSearch(value: string) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR").trim();
}
